import { useMemo, useState } from "react";
import { Check, Copy, MonitorUp, Video } from "lucide-react";
import { findMeetingLink, meetingTextFromHtml } from "@/lib/meetingLinks";

/**
 * "Join" card above a message that carries an online-meeting link (Teams, Zoom, Google Meet, Webex,
 * Zoho, GoTo, …): the platform, a Join button, "Open in app" where the platform documents a direct
 * app link, and the meeting ID / passcode with copy buttons — like Outlook's meeting banner.
 */
export function MeetingJoinCard({ html, className = "" }: { html: string; className?: string }) {
    const link = useMemo(() => findMeetingLink(meetingTextFromHtml(html)), [html]);
    if (!link) return null;

    return (
        <div
            role="region"
            aria-label={`${link.provider} meeting`}
            className={`flex flex-wrap items-center gap-x-4 gap-y-2 rounded-lg border bg-muted/40 px-4 py-3 ${className}`}
        >
            <div className="flex min-w-0 items-center gap-3">
                <span
                    className="flex h-9 w-9 shrink-0 items-center justify-center rounded-md text-white"
                    style={{ background: link.color }}
                    aria-hidden
                >
                    <Video className="h-5 w-5" />
                </span>
                <div className="min-w-0">
                    <p className="text-sm font-semibold">{link.provider} meeting</p>
                    {link.meetingId || link.passcode ? (
                        <div className="flex flex-wrap items-center gap-x-4 gap-y-0.5 text-xs text-muted-foreground">
                            {link.meetingId ? <CopyValue label="Meeting ID" value={link.meetingId} /> : null}
                            {link.passcode ? <CopyValue label="Passcode" value={link.passcode} /> : null}
                        </div>
                    ) : null}
                </div>
            </div>
            <div className="ml-auto flex flex-wrap items-center gap-2">
                <a
                    href={link.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    title={link.url}
                    className="inline-flex h-9 items-center rounded-md px-4 text-sm font-semibold text-white shadow-sm hover:opacity-90 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                    style={{ background: link.color }}
                >
                    {link.label}
                </a>
                {link.appUrl ? (
                    <a
                        href={link.appUrl}
                        title={`Open in the ${link.provider} desktop app`}
                        className="inline-flex h-9 items-center gap-1.5 rounded-md border bg-background px-3 text-sm font-medium hover:bg-accent focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                    >
                        <MonitorUp className="h-4 w-4" aria-hidden /> Open in app
                    </a>
                ) : null}
                <CopyButton value={link.url} />
            </div>
        </div>
    );
}

function useCopy(value: string) {
    const [done, setDone] = useState(false);
    const copy = () => {
        void navigator.clipboard?.writeText(value).then(() => {
            setDone(true);
            window.setTimeout(() => setDone(false), 1500);
        }, () => undefined);
    };
    return { done, copy };
}

function CopyValue({ label, value }: { label: string; value: string }) {
    const { done, copy } = useCopy(value);
    return (
        <span className="inline-flex items-center gap-1">
            {label}: <span className="font-medium text-foreground">{value}</span>
            <button
                type="button"
                onClick={copy}
                title={`Copy ${label.toLowerCase()}`}
                aria-label={`Copy ${label.toLowerCase()}`}
                className="rounded p-0.5 hover:bg-accent hover:text-foreground"
            >
                {done ? <Check className="h-3 w-3 text-emerald-600" /> : <Copy className="h-3 w-3" />}
            </button>
        </span>
    );
}

function CopyButton({ value }: { value: string }) {
    const { done, copy } = useCopy(value);
    return (
        <button
            type="button"
            onClick={copy}
            className="inline-flex h-9 items-center gap-1.5 rounded-md border bg-background px-3 text-sm font-medium hover:bg-accent focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
        >
            {done ? <Check className="h-4 w-4 text-emerald-600" aria-hidden /> : <Copy className="h-4 w-4" aria-hidden />}
            {done ? "Copied" : "Copy link"}
        </button>
    );
}
